import {
  faFolder,
  faLayerGroup,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import SubtleBadge from 'components/common/SubtleBadge';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import Pagination from 'components/pagination';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Dropdown, Row } from 'react-bootstrap';
import { PiFoldersFill } from 'react-icons/pi';
import Skeleton from 'react-loading-skeleton';
import { Link, useNavigate } from 'react-router-dom';
import KbArticlesService from 'services/KbArticles.service';
import KbFolderArticlesService from 'services/KbFolderArticles.service';
import KbFoldersService from 'services/KbFolders.service';
import KbGroupFoldersSevice from 'services/KbGroupFolders.service';
import KbGroupsService from 'services/KbGroups.service';
import KbFolderRegistrationModal from '../modals/KbFolderRegistrationModal';
import KbGroupEditModal from '../modals/KbGroupEditModal';
import KbGroupRegistrationModal from '../modals/KbGroupRegistrationModal';
import ArticleSearchResults from './ArticleSearchResults';
import ArticleTreeView from './ArticlesTreeView';

const ArticleList = () => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const formatDateTime = useFormatDateTime();
  // Estados principais
  const [groups, setGroups] = useState([]);
  const [groupFolders, setGroupFolders] = useState({}); // Armazena pastas por grupo
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);

  // Estados da árvore
  const [expanded, setExpanded] = useState([]);
  const [treeSelectedItems, setTreeSelectedItems] = useState([]);
  const [kbGroups, setKbGroups] = useState([]);

  // Estados de seleção
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [selectedGroupName, setSelectedGroupName] = useState('Todas as pastas');

  // Estados dos modais
  const [showGroupRegistrationModal, setShowGroupRegistrationModal] =
    useState(false);
  const [showGroupEditModal, setShowGroupEditModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [selectedKbGroup, setSelectedKbGroup] = useState(null);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [isEditingFolder, setIsEditingFolder] = useState(false);

  // Estados da tabela e pesquisa
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [knowledgeBaseData, setKnowledgeBaseData] = useState({
    content: [],
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    searchTerm: '',
    group: ''
  });

  // Estados para controle de cache e loading
  const [loadingGroups, setLoadingGroups] = useState(new Set()); // Grupos que estão sendo carregados
  const foldersCacheRef = useRef(new Set()); // Cache para controlar quais grupos já foram buscados
  const articlesCacheRef = useRef({}); // Cache para artigos por pasta
  const [loadingArticles, setLoadingArticles] = useState(false); // Loading dos artigos

  const navigate = useNavigate();

  // Função para buscar os grupos
  const fetchGroups = async () => {
    try {
      setIsLoading(true);
      const response = await KbGroupsService.list();
      setGroups(response || []);
      return response || [];
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
      setGroups([]);
      return [];
    }
  };

  const fetchGroupsWithFolders = async () => {
    try {
      setIsLoading(true);
      const response = await KbGroupsService.listWithFolders();
      setKbGroups(response || []);
    } catch (error) {
      console.error('Erro ao buscar grupos com pastas:', error);
      setKbGroups([]);
    }
  };

  // Função para buscar as pastas de um grupo específico com cache
  const fetchFolders = async (groupId, forceRefresh = false) => {
    // Se já está carregando este grupo, não fazer nova requisição
    if (loadingGroups.has(groupId)) {
      return groupFolders[groupId] || [];
    }

    // Se já foi buscado e não é refresh forçado, retornar do cache
    if (foldersCacheRef.current.has(groupId) && !forceRefresh) {
      return groupFolders[groupId] || [];
    }

    try {
      // Marcar como carregando
      setLoadingGroups(prev => new Set([...prev, groupId]));

      const response = await KbGroupFoldersSevice.list(groupId);
      const folders = Array.isArray(response) ? response : [];

      setGroupFolders(prev => ({
        ...prev,
        [groupId]: folders
      }));

      // Adicionar ao cache
      foldersCacheRef.current.add(groupId);

      return folders;
    } catch (error) {
      console.error('Erro ao buscar pastas:', error);
      setGroupFolders(prev => ({
        ...prev,
        [groupId]: []
      }));
      return [];
    } finally {
      // Remover do loading
      setLoadingGroups(prev => {
        const newSet = new Set(prev);
        newSet.delete(groupId);
        return newSet;
      });
    }
  };

  // Função para buscar artigos de uma pasta com cache
  const fetchArticles = async (folderId, forceRefresh = false) => {
    try {
      if (!folderId) {
        setKnowledgeBaseData({ content: [], totalElements: 0 });
        setTableData([]);
        return;
      }

      // Criar chave única para o cache incluindo filtros relevantes
      const cacheKey = `${folderId}_${filters.page}_${filters.size}_${filters.searchTerm}`;

      // Se existe no cache e não é refresh forçado, usar dados do cache
      if (articlesCacheRef.current[cacheKey] && !forceRefresh) {
        const cachedData = articlesCacheRef.current[cacheKey];
        setKnowledgeBaseData(cachedData);
        setTableData(cachedData.content || []);
        return;
      }

      setLoadingArticles(true);
      const queryParams = new URLSearchParams(filters).toString();
      const response = await KbFolderArticlesService.list(
        folderId,
        queryParams
      );

      if (response) {
        // Armazenar no cache
        articlesCacheRef.current[cacheKey] = response;

        setKnowledgeBaseData(response);
        setTableData(response.content || []);
      }
    } catch (error) {
      console.error('Erro ao buscar artigos:', error);
      setKnowledgeBaseData({ content: [], totalElements: 0 });
      setTableData([]);
    } finally {
      setLoadingArticles(false);
    }
  };

  // Processamento dos dados para o treeview com pastas carregadas
  const treeviewItems = useMemo(() => {
    return groups.map(group => ({
      name: group.name,
      id: group.id,
      description: group.description || '',
      icon: faLayerGroup,
      isLoading: loadingGroups.has(group.id),
      children: (groupFolders[group.id] || []).map(folder => ({
        name: folder.name,
        id: folder.id,
        icon: faFolder,
        kbGroup: { id: group.id, name: group.name },
        groupId: group.id,
        visibleTo: folder.visibleTo || '',
        description: folder.description || '',
        agents: folder.agents || [],
        customers: folder.customers || [],
        children: [] // Necessário para o ArticleTreeView identificar como item filho
      }))
    }));
  }, [groups, groupFolders, loadingGroups]);

  // Effect para carregar dados iniciais e selecionar primeira pasta
  useEffect(() => {
    const loadInitialData = async () => {
      setIsFirstLoading(true);
      const groupsData = await fetchGroups();

      if (groupsData.length > 0) {
        const firstGroup = groupsData[0];
        setSelectedGroup(firstGroup.id);
        setSelectedGroupName(firstGroup.name);

        // Buscar pastas do primeiro grupo
        const folders = await fetchFolders(firstGroup.id);

        if (folders.length > 0) {
          const firstFolder = folders[0];
          setSelectedFolder(firstFolder.id);
          // setTreeSelectedItems([firstFolder.id]);
          setExpanded([firstGroup.id]);
          setSelectedGroupName(`${firstGroup.name} / ${firstFolder.name}`);

          // Buscar artigos da primeira pasta
          setFilters(prev => ({ ...prev, group: firstFolder.id }));
        }
      }

      setIsFirstLoading(false);
      setIsLoading(false);
    };

    loadInitialData();
    fetchGroupsWithFolders();
  }, []);

  // Effect para buscar artigos quando o filtro de grupo muda
  useEffect(() => {
    if (selectedFolder && !isFirstLoading) {
      fetchArticles(selectedFolder, true);
    }

    // Limpar dados quando não houver grupo selecionado
    if (!selectedFolder) {
      setKnowledgeBaseData({ content: [], totalElements: 0 });
      setTableData([]);
    }
  }, [selectedFolder, filters]);

  // Effect para aplicar delay na pesquisa
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm, page: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Função para lidar com cliques no treeview
  const handleTreeviewItemClick = async item => {
    // Se o item tem children e não é uma pasta (é um grupo)
    if (item.children !== undefined && item.icon === faLayerGroup) {
      const isCurrentlyExpanded = expanded.includes(item.id);

      if (!isCurrentlyExpanded) {
        // Expandindo o grupo - buscar pastas se necessário
        await fetchFolders(item.id);

        setExpanded(prev => [...prev, item.id]);

        // Auto-selecionar primeira pasta se existir
        const folders = groupFolders[item.id] || [];
        if (folders.length > 0) {
          const firstFolder = folders[0];
          setSelectedFolder(firstFolder.id);
          // setTreeSelectedItems([firstFolder.id]);
          setFilters(prev => ({ ...prev, group: firstFolder.id, page: 0 }));
          setSelectedGroupName(`${item.name} / ${firstFolder.name}`);
        } else {
          setSelectedGroupName(item.name);
        }
      } else {
        // Contraindo o grupo
        setExpanded(prev => prev.filter(id => id !== item.id));
        setSelectedGroupName(item.name);
      }

      setSelectedGroup(item.id);
    }
    // Se é uma pasta (não tem children ou tem children vazio)
    else {
      setSelectedFolder(item.id);
      // setTreeSelectedItems([item.id]);
      setFilters(prev => ({ ...prev, group: item.id, page: 0 }));

      // Atualiza o nome da pasta selecionada
      const parentGroup = groups.find(
        g => g.id === (item.groupId || selectedGroup)
      );
      if (parentGroup) {
        setSelectedGroupName(`${parentGroup.name} / ${item.name}`);
      }
    }
  };

  // Função para recarregar dados e limpar caches
  const getData = async () => {
    const groupsData = await fetchGroups();

    // Limpar todos os caches para forçar atualização
    foldersCacheRef.current.clear();
    articlesCacheRef.current = {};
    setGroupFolders({});

    if (groupsData.length > 0 && selectedGroup) {
      // Se há um grupo selecionado, recarregar suas pastas
      await fetchFolders(selectedGroup, true);

      if (selectedFolder) {
        await fetchArticles(selectedFolder, true);
      }
    }
  };

  // Função para limpar cache quando necessário (pode ser chamada externamente)
  const clearFoldersCache = (groupId = null) => {
    if (groupId) {
      foldersCacheRef.current.delete(groupId);
      setGroupFolders(prev => {
        const newState = { ...prev };
        delete newState[groupId];
        return newState;
      });
    } else {
      foldersCacheRef.current.clear();
      setGroupFolders({});
    }
  };

  // Função para limpar cache de artigos
  const clearArticlesCache = (folderId = null) => {
    if (folderId) {
      // Limpar todas as entradas relacionadas à pasta específica
      Object.keys(articlesCacheRef.current).forEach(key => {
        if (key.startsWith(`${folderId}_`)) {
          delete articlesCacheRef.current[key];
        }
      });
    } else {
      articlesCacheRef.current = {};
      setKnowledgeBaseData({ content: [], totalElements: 0 });
      setTableData([]);
    }
  };

  // Função para invalidar cache após operações CRUD
  const invalidateCache = async (type = 'all', id = null) => {
    switch (type) {
      case 'folder':
        clearFoldersCache(id);
        break;
      case 'article':
        clearArticlesCache(id);
        break;
      case 'group':
        fetchGroups();
        break;
      case 'folderDeleted':
        await handleFolderDeletion(id);
        break;
      case 'all':
      default:
        clearFoldersCache();
        clearArticlesCache();
        await getData();
        break;
    }
  };

  const handleFolderDeletion = async deletedFolderId => {
    try {
      // Atualizar grupos primeiro
      const groupsData = await fetchGroups();

      // Limpar caches
      foldersCacheRef.current.clear();
      articlesCacheRef.current = {};
      setGroupFolders({});

      // Se a pasta excluída era a pasta selecionada
      if (selectedFolder === deletedFolderId) {
        // Buscar pastas do grupo atual
        if (selectedGroup && groupsData.length > 0) {
          const folders = await fetchFolders(selectedGroup, true);

          if (folders.length > 0) {
            // Selecionar primeira pasta disponível
            const firstFolder = folders[0];
            setSelectedFolder(firstFolder.id);
            // setTreeSelectedItems([firstFolder.id]);
            setFilters(prev => ({ ...prev, group: firstFolder.id, page: 0 }));

            // Atualizar nome da pasta selecionada
            const parentGroup = groupsData.find(g => g.id === selectedGroup);
            if (parentGroup) {
              setSelectedGroupName(`${parentGroup.name} / ${firstFolder.name}`);
            }

            // Buscar artigos da nova pasta selecionada
            await fetchArticles(firstFolder.id, true);
          } else {
            // Não há mais pastas no grupo - limpar seleção
            setSelectedFolder('');
            setTreeSelectedItems([]);
            setKnowledgeBaseData({ content: [], totalElements: 0 });
            setTableData([]);
            setFilters(prev => ({ ...prev, group: '', page: 0 }));

            // Mostrar apenas o nome do grupo
            const parentGroup = groupsData.find(g => g.id === selectedGroup);
            if (parentGroup) {
              setSelectedGroupName(parentGroup.name);
            }
          }
        } else {
          // Não há grupos ou grupo selecionado não existe mais
          setSelectedGroup('');
          setSelectedFolder('');
          setTreeSelectedItems([]);
          setKnowledgeBaseData({ content: [], totalElements: 0 });
          setTableData([]);
          setFilters(prev => ({ ...prev, group: '', page: 0 }));
          setSelectedGroupName('Todas as pastas');
        }
      } else {
        // A pasta excluída não era a selecionada - apenas atualizar o grupo
        if (selectedGroup) {
          await fetchFolders(selectedGroup, true);
        }
      }
    } catch (error) {
      console.error('Erro ao lidar com exclusão de pasta:', error);
    }
  };

  // Configuração das colunas da tabela
  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Título',
        cell: rowData => {
          const { id, title, status } = rowData.row.original;
          const isDraft = status?.status && status?.status === 'DRAFT';
          return (
            <Flex>
              <Link
                to={`/documents/articles/${id}`}
                className="fw-semi-bold fs-9"
                style={{
                  maxWidth: '250px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {title}
              </Link>
              {isDraft && (
                <SubtleBadge className={'ms-2'} bg="warning">
                  Rascunho
                </SubtleBadge>
              )}
            </Flex>
          );
        },
        meta: {
          headerProps: { className: 'pe-1 text-900' },
          cellProps: { className: 'py-2' }
        }
      },
      {
        accessorKey: 'user',
        header: 'Criado por',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { user } = rowData.row.original;

          return (
            <span
              className="text-start d-block text-700 fs-9"
              style={{
                maxWidth: '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user ? `${user.firstName} ${user.lastName}` : 'Sistema'}
            </span>
          );
        }
      },
      {
        accessorKey: 'creationDate',
        header: 'Data Criação',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { creationDate } = rowData.row.original;

          return (
            <span
              className="text-start text-700 fs-9"
              style={{
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {creationDate ? formatDateTime(creationDate) : '---'}
            </span>
          );
        }
      },
      {
        accessorKey: 'lastUpdateDate',
        header: 'Data Atualização',
        meta: {
          headerProps: { className: 'text-900' }
        },
        cell: rowData => {
          const { lastUpdateDate } = rowData.row.original;

          return (
            <span
              className="text-start text-700 fs-9"
              style={{
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {lastUpdateDate ? formatDateTime(lastUpdateDate) : '---'}
            </span>
          );
        }
      }
    ],
    []
  );

  // Função para lidar com a edição de pastas
  const handleEditFolder = item => {
    const folderData = {
      id: item.id,
      name: item.name,
      kbGroup: item.kbGroup,
      visibleTo: item.visibleTo,
      description: item.description,
      agents: item.agents ? item.agents : [],
      customers: item.customers ? item.customers : []
    };

    setFolderToEdit(folderData);
    setIsEditingFolder(true);
    setShowFolderModal(true);
  };

  // Função para lidar com a edição de grupos
  const handleEditGroup = item => {
    const groupData = {
      id: item.id,
      name: item.name,
      description: item.description
    };

    setSelectedKbGroup(groupData);
    setShowGroupEditModal(true);
  };

  // Handlers para os modais com invalidação de cache
  // Handlers para os modais - Versão corrigida
  const handleCloseFolderModal = () => {
    setShowFolderModal(false);
    setFolderToEdit(null);
    setIsEditingFolder(false);
  };

  const handleNewFolderClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsEditingFolder(false);
    setFolderToEdit(null);
    setShowFolderModal(true);
  };

  const handleNewGroupClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setShowGroupRegistrationModal(true);
  };

  // Handler específico para refresh após operações de grupo
  const handleGroupRefresh = async () => {
    try {
      await invalidateCache('group');
    } catch (error) {
      console.error('Erro ao atualizar grupos:', error);
    }
  };

  // Handler para mover artigos selecionados com invalidação de cache
  const handleMoveArticlesToFolder = async targetFolder => {
    const selectedArticleIds = table
      .getSelectedRowModel()
      .flatRows.map(row => row.original.id);

    const payload = {
      kbArticleIds: selectedArticleIds,
      targetKbFolder: { id: targetFolder }
    };

    try {
      // Implementar chamada à API para mover os artigos
      const req = await KbArticlesService.moveArticlesToFolder(payload);

      if (req.ok) {
        showSuccessNotification('Artigos movidos com sucesso');
        clearArticlesCache();

        // Recarregar artigos da pasta atual
        if (selectedFolder) {
          await fetchArticles(selectedFolder, true);
        }

        // Limpar a seleção
        table.resetRowSelection();
      } else {
        showErrorNotification(req.userMessage || 'Erro ao mover artigos');
      }
    } catch (error) {
      console.error('Erro ao mover artigos:', error);
    }
  };

  const handleMoveFoldersToGroup = async targetGroupId => {
    // Pegar os IDs das pastas selecionadas
    const selectedFolderIds = treeSelectedItems.filter(id =>
      // Filtra apenas os IDs que são de pastas (não de grupos)
      treeviewItems.some(group =>
        group.children?.some(folder => folder.id === id)
      )
    );

    if (selectedFolderIds.length === 0) {
      showErrorNotification('Selecione pelo menos uma pasta para mover');
      return;
    }

    const payload = {
      kbFolderIds: selectedFolderIds,
      targetKbGroup: { id: targetGroupId }
    };

    console.log('Payload para mover pastas:', payload);

    try {
      // Implementar chamada à API para mover as pastas
      const req = await KbFoldersService.moveFoldersToGroup(payload);

      if (req.ok) {
        showSuccessNotification('Pastas movidas com sucesso');
        // Limpar a seleção das pastas
        setTreeSelectedItems([]);
        // Invalidar cache e recarregar dados
        await invalidateCache('all');
      } else {
        showErrorNotification(req.userMessage || 'Erro ao mover pastas');
      }
    } catch (error) {
      console.error('Erro ao mover pastas:', error);
      showErrorNotification('Erro ao mover pastas');
    }
  };

  const table = useAdvanceTable({
    data: tableData,
    columns,
    sortable: true,
    selection: true
  });

  const isEmpty = !isFirstLoading && knowledgeBaseData.content.length === 0;
  const hasSelectedArticles = table.getSelectedRowModel().flatRows.length > 0;

  return (
    <>
      <KbGroupRegistrationModal
        showModal={showGroupRegistrationModal}
        handleClose={() => setShowGroupRegistrationModal(false)}
        refresh={handleGroupRefresh}
      />
      <KbGroupEditModal
        showModal={showGroupEditModal}
        handleClose={() => setShowGroupEditModal(false)}
        refresh={handleGroupRefresh}
        groupToEdit={selectedKbGroup}
      />
      <KbFolderRegistrationModal
        showModal={showFolderModal}
        handleClose={handleCloseFolderModal}
        isSuccess={() => invalidateCache('folder', selectedGroup)}
        groups={groups}
        isEditing={isEditingFolder}
        folderToEdit={folderToEdit}
        refreshFolderDeleted={deletedFolderId =>
          invalidateCache('folderDeleted', deletedFolderId)
        }
      />

      <FalconComponentCard style={{ minHeight: '87vh' }}>
        <Card.Header>
          <Flex
            className={'w-100'}
            justifyContent={'between'}
            alignItems={'center'}
          >
            <div className="w-25">
              <ArticleSearchResults />
            </div>

            <Flex>
              <Button
                size="sm"
                variant="primary"
                style={{ borderRadius: '20px 0px 0px 20px' }}
                onClick={() => navigate('/documents/articles/new')}
              >
                <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
                Novo artigo
              </Button>

              <Dropdown>
                <Dropdown.Toggle
                  type="button"
                  variant="primary"
                  size="sm"
                  style={{
                    borderRadius: '0px 20px 20px 0px',
                    height: '100%'
                  }}
                />
                <Dropdown.Menu style={{ width: '150px' }}>
                  <Dropdown.Item
                    as={Button}
                    variant="link"
                    className="text-muted fs-10"
                    type="button"
                    onClick={handleNewFolderClick}
                  >
                    Nova pasta
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Button}
                    variant="link"
                    className="text-muted fs-10"
                    type="button"
                    onClick={handleNewGroupClick}
                  >
                    Novo Grupo
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Flex>
          </Flex>
        </Card.Header>

        <FalconComponentCard.Body>
          <Row className="h-100">
            <Col md={3} style={{ borderRight: '1px solid #B6C2D2' }}>
              <Flex direction="column">
                <Flex
                  justifyContent="between"
                  alignItems="center"
                  className="mb-2"
                  style={{ width: '100%' }}
                >
                  <h4 className="mb-0">Grupos e Pastas</h4>

                  {treeSelectedItems.length > 0 && (
                    <Dropdown className="ms-3">
                      <Dropdown.Toggle
                        style={{ borderRadius: '5px' }}
                        variant="falcon-default"
                        size="sm"
                      >
                        <span className="me-2">Mover pastas</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ width: '250px' }}>
                        {groups.map(group => (
                          <Dropdown.Item
                            key={group.id}
                            className="text-secondary border-bottom border-1"
                            onClick={() => handleMoveFoldersToGroup(group.id)}
                          >
                            <PiFoldersFill className="me-2" />
                            {group.name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}

                  {hasSelectedArticles && (
                    <Dropdown className="ms-3">
                      <Dropdown.Toggle
                        style={{ borderRadius: '5px' }}
                        variant="falcon-default"
                        size="sm"
                      >
                        <span className="me-2">Mover artigos</span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu style={{ width: '250px' }}>
                        {kbGroups.map(group => (
                          <React.Fragment key={group.id}>
                            <Dropdown.Header className="fw-bolder">
                              {group.name}
                            </Dropdown.Header>
                            {group.kbFolders.map(folder => (
                              <Dropdown.Item
                                className="text-secondary border-bottom border-1"
                                key={folder.id}
                                onClick={() =>
                                  handleMoveArticlesToFolder(folder.id)
                                }
                              >
                                <FontAwesomeIcon
                                  icon={faFolder}
                                  className="me-2"
                                  style={{ color: '#0C446E' }}
                                />
                                {folder.name}
                              </Dropdown.Item>
                            ))}
                          </React.Fragment>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </Flex>

                {isFirstLoading ? (
                  <Skeleton count={5} height={30} baseColor="#5E6E82" />
                ) : (
                  <ArticleTreeView
                    selection={true}
                    selectedGroup={selectedGroup}
                    selectedFolder={selectedFolder}
                    data={treeviewItems}
                    expanded={expanded}
                    selectedItems={treeSelectedItems}
                    setSelectedItems={setTreeSelectedItems}
                    onClick={handleTreeviewItemClick}
                    onEdit={handleEditFolder}
                    onGroupEdit={handleEditGroup}
                  />
                )}
              </Flex>
            </Col>

            <Col md={9} className="h-100">
              <Flex
                justifyContent="between"
                alignItems="center"
                className="mb-3"
              >
                <h5 className="mb-0">
                  <strong>{selectedGroupName}</strong>
                </h5>
              </Flex>

              {isFirstLoading || loadingArticles ? (
                <Skeleton count={5} height={53} baseColor="#5E6E82" />
              ) : isEmpty ? (
                <EmptyList imgSize={400} vhSize={73} />
              ) : (
                <div className="d-flex flex-column">
                  <div className="flex-grow-1">
                    <AdvanceTableProvider {...table}>
                      <AdvanceTable
                        bodyClassName="min-height-100"
                        table
                        headerClassName="text-nowrap align-middle"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                          size: 'sm',
                          striped: true,
                          className: 'fs-9 mb-0 overflow-hidden'
                        }}
                      />
                    </AdvanceTableProvider>
                  </div>

                  <Card.Footer>
                    <Pagination
                      data={knowledgeBaseData}
                      filters={filters}
                      setFilters={setFilters}
                    />
                  </Card.Footer>
                </div>
              )}
            </Col>
          </Row>
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default ArticleList;
